import { resolveOpenerLocale, shopperVolumeGain, sizeItemImage } from "@loot-os/api-contract";
import { Toaster } from "@loot-os/design-system/core/Toast";
import { CurrencyProvider } from "@loot-os/design-system/domain/CurrencyProvider";
import { ImageProvider } from "@loot-os/design-system/foundations/ImageProvider";
import { LinkProvider } from "@loot-os/design-system/foundations/LinkProvider";
import { ThemeProvider } from "@loot-os/design-system/foundations/ThemeProvider";
import { LootProvider, liveBreakSounds } from "@loot-os/react";
import { Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { StoreChat } from "./components/chat/StoreChat.js";
import { IntercomLauncher } from "./components/IntercomLauncher.js";
import { LiveBreaksDock } from "./components/liveBreaks/LiveBreaksDock.js";
import { ProfileProvider } from "./components/ProfileProvider.js";
import { RouterLink } from "./components/RouterLink.js";
import { StoreDeposit } from "./components/StoreDeposit.js";
import { StoreFrame } from "./components/StoreFrame.js";
import { useChatAvailable } from "./lib/chat.js";
import { LOOT_PROXY_BASE, PROXIED_PUBLISHABLE_KEY } from "./lib/config.js";
import { mintSessionToken } from "./lib/queries.js";
import { useShopper } from "./lib/shopper.js";
import { soundsOf, useSoundPreference } from "./lib/soundPreference.js";
import { useTenant } from "./lib/tenant.js";
import { useTerminology } from "./lib/terminology.js";
import { toLootTheme } from "./lib/theme.js";
import { storeToasts } from "./lib/toasts.js";

/**
 * The pages drawn from a design-system template, which lay out their own width. Any other route
 * (the router's not-found page) sits in a centered column.
 */
const TEMPLATED_PATHS = new Set([
  "/",
  "/boxes",
  "/login",
  "/fairness",
  "/terms",
  "/privacy",
  "/battles",
  "/battles/create",
  "/live-breaks",
  "/account",
  "/inventory",
]);

/**
 * The templated pages on a dynamic route, by a pattern over the path: a box, the Forge's four, a
 * battle's room, and a live break's page (and the live-break pages under the directory).
 */
const TEMPLATED_PATTERNS: readonly RegExp[] = [
  /^\/boxes\/[^/]+$/,
  /^\/forge(\/|$)/,
  /^\/battles\/[^/]+$/,
  /^\/live-breaks\/.+/,
];

function isTemplatedPath(pathname: string): boolean {
  return TEMPLATED_PATHS.has(pathname) || TEMPLATED_PATTERNS.some((p) => p.test(pathname));
}

export function App() {
  const { sessionToken, client, authPending, sessionPending } = useShopper();
  const { config } = useTenant();
  const terms = useTerminology();
  // The opener's own labels ("Claim items") in the store's words; memoized so the provider's
  // value is stable across renders.
  const locale = useMemo(() => resolveOpenerLocale(terms), [terms]);
  // A store with chat off shows neither the tab nor the panel, not even briefly: see
  // `useChatAvailable` for why the config alone is not enough.
  const chat = useChatAvailable();
  const sound = useSoundPreference();
  const soundEvents = soundsOf(config);
  const templated = useLocation({ select: (location) => isTemplatedPath(location.pathname) });
  // The live-break cues share the openers' engine, so they follow the shopper's level too.
  useEffect(() => {
    liveBreakSounds.setMuted(sound.muted);
    liveBreakSounds.setVolume(shopperVolumeGain(sound.volume));
  }, [sound.muted, sound.volume]);

  return (
    <LootProvider
      publishableKey={PROXIED_PUBLISHABLE_KEY}
      baseUrl={LOOT_PROXY_BASE}
      // Signed in, share the app's client (one instance, one token). Signed
      // out it must stay unset — the opener's demo mode requires no client.
      client={sessionToken ? client : undefined}
      sessionToken={sessionToken}
      // Until the auth check and the token mint have both answered, the token's absence means
      // "unknown", not "signed out": the opener waits instead of offering a demo open.
      sessionPending={authPending || sessionPending}
      onSessionExpired={mintSessionToken}
      theme={toLootTheme(config.theme)}
      currency={config.currency}
      locale={locale}
      soundEvents={soundEvents}
      soundMuted={sound.muted}
      soundVolume={sound.volume}
      onSoundMutedChange={sound.setMuted}
    >
      {/* The theme is already on <html> (TenantConfigProvider); ThemeProvider shares its layout
          choices with the design system's components. */}
      <ThemeProvider theme={config.theme}>
        {/* The one toast outlet: saves, refusals and arriving notifications, from React or not. */}
        <Toaster manager={storeToasts.manager}>
          <LinkProvider component={RouterLink}>
            {/* Item photos at the size they're drawn, where their host serves sizes. */}
            <ImageProvider size={sizeItemImage}>
              <CurrencyProvider currency={config.currency}>
                {/* Inside the root route: the profile dialog links to the Account page, which needs
                  the router; a provider above <RouterProvider> would have no router to link with. */}
                <ProfileProvider>
                  <StoreFrame chat={chat}>
                    {templated ? (
                      <Outlet />
                    ) : (
                      <div className="mx-auto w-full max-w-store min-w-0 flex-1 px-4 py-8 [--store-max:80rem]">
                        <Outlet />
                      </div>
                    )}
                  </StoreFrame>
                  {/* The chat: where the theme puts it in a drawer, inset at the right under the
                    header on a wide screen and a sheet on a phone, opened by the header's launcher
                    and the tab bar's Chat. Where it docks chat, StoreFrame draws the dock and this
                    keeps the shopper's presence. */}
                  {chat && <StoreChat />}
                  {/* The live-breaks widget and its picture-in-picture player, at the bottom-left
                    corner on every page (the rows hide on a live break's own page, the player stays).
                    Here, beside the frame rather than inside a page, so navigating never remounts the
                    player: a remount restarts the stream. */}
                  <LiveBreaksDock />
                  {/* The Deposit dialog: the header's Deposit and balance, every "Buy" link, ?deposit=1 and
                    Stripe's checkout return open it over the page. */}
                  <StoreDeposit />
                  {/* Fixed-position, so where it sits in the tree is cosmetic; it needs both providers. */}
                  <IntercomLauncher />
                </ProfileProvider>
              </CurrencyProvider>
            </ImageProvider>
          </LinkProvider>
        </Toaster>
      </ThemeProvider>
    </LootProvider>
  );
}
